import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, XCircle } from "lucide-react";

import TeamDropdown from "../Utils/TeamDropdown";

// =========================================================
// CREATE DRIVER SLOT MODAL
// =========================================================

export const CreateDriverSlotV2Modal = ({
  open,
  onOpenChange,
  selectedDate,
  onDateChange,
  minDate,
  teams,
  selectedTeam,
  onTeamChange,
  onCreate,
  loading = false,
}) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />

        <Dialog.Content className="fixed left-1/2 top-1/2 z-[60] w-[calc(100%-24px)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-5 shadow-2xl">
          <Dialog.Title className="text-lg font-semibold text-gray-900">
            Create Driver Slot
          </Dialog.Title>

          <Dialog.Description className="mt-1 text-sm text-gray-500">
            Select the date and team for which you want to create the slot.
          </Dialog.Description>

          {/* DATE */}

          <div className="mt-5">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Select Date
            </label>

            <input
              type="date"
              min={minDate}
              value={selectedDate}
              onChange={(event) => onDateChange(event.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>

          {/* TEAM */}

          <div className="mt-5">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Select Team
            </label>

            <TeamDropdown
              teams={teams}
              value={selectedTeam}
              onChange={onTeamChange}
              placeholder="-- Select Team --"
            />
          </div>

          {/* ACTIONS */}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="h-10 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={!selectedDate || !selectedTeam || loading}
              onClick={onCreate}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-orange-500 px-5 text-sm font-semibold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading && <Loader2 size={15} className="animate-spin" />}

              {loading ? "Creating..." : "Create"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// =========================================================
// CONFIGURE TIME WINDOW MODAL
// =========================================================

export const ConfigureTimeWindowModal = ({
  open,
  onOpenChange,
  editingConfig,
  configForm,
  onConfigChange,
  teams,
  saving,
  onSave,
}) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />

        <Dialog.Content className="fixed left-1/2 top-1/2 z-[60] max-h-[90vh] w-[calc(100%-24px)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl">
          {/* HEADER */}

          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {editingConfig ? "Update Time Window" : "Configure Time Window"}
              </Dialog.Title>

              <Dialog.Description className="mt-1 text-sm text-gray-500">
                {editingConfig
                  ? "Update the time window, driver capacity or status."
                  : "Create a time window configuration for driver slots."}
              </Dialog.Description>
            </div>

            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <XCircle size={20} />
              </button>
            </Dialog.Close>
          </div>

          {/* FORM */}

          <div className="mt-5 space-y-4">
            {/* START TIME */}

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Start Time
              </label>

              <input
                type="time"
                value={configForm.startTime}
                disabled={!!editingConfig}
                onChange={(event) =>
                  onConfigChange("startTime", event.target.value)
                }
                className={`h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none ${
                  editingConfig
                    ? "cursor-not-allowed bg-gray-100 opacity-70"
                    : "bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                }`}
              />

              <p className="mt-1 text-xs text-gray-400">24-hour format</p>
            </div>

            {/* END TIME */}

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                End Time
              </label>

              <input
                type="time"
                value={configForm.endTime}
                disabled={!!editingConfig}
                onChange={(event) =>
                  onConfigChange("endTime", event.target.value)
                }
                className={`h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none ${
                  editingConfig
                    ? "cursor-not-allowed bg-gray-100 opacity-70"
                    : "bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                }`}
              />

              <p className="mt-1 text-xs text-gray-400">24-hour format</p>
            </div>

            {/* TEAM */}

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Select Team
              </label>

              <div
                className={
                  editingConfig ? "pointer-events-none opacity-70" : ""
                }
              >
                <TeamDropdown
                  teams={teams}
                  value={configForm.teamId}
                  onChange={(value) => onConfigChange("teamId", value)}
                  placeholder="-- Select Team --"
                />
              </div>
            </div>

            {/* DURATION */}

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Slot Duration
              </label>

              <select
                value={configForm.slotDurationMinutes}
                onChange={(event) =>
                  onConfigChange("slotDurationMinutes", event.target.value)
                }
                className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              >
                <option value="">-- Select Duration --</option>

                <option value="30">30 minutes</option>

                <option value="45">45 minutes</option>

                <option value="60">60 minutes</option>

                <option value="90">90 minutes</option>

                <option value="120">120 minutes</option>
              </select>
            </div>

            {/* CAPACITY */}

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Maximum Driver Capacity
              </label>

              <input
                type="number"
                min="1"
                value={configForm.maxDriverCapacity}
                onChange={(event) =>
                  onConfigChange("maxDriverCapacity", event.target.value)
                }
                placeholder="Enter maximum drivers"
                className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>

            {/* ACTIVE */}

            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-800">Active</p>

                <p className="text-xs text-gray-500">Enable this time window</p>
              </div>

              <button
                type="button"
                onClick={() =>
                  onConfigChange(
                    "isActive",
                    Number(configForm.isActive) === 1 ? 0 : 1,
                  )
                }
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
                  Number(configForm.isActive) === 1
                    ? "bg-green-500"
                    : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                    Number(configForm.isActive) === 1
                      ? "translate-x-6"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* FOOTER */}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="h-10 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-orange-500 px-5 text-sm font-semibold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving && <Loader2 size={15} className="animate-spin" />}

              {saving
                ? editingConfig
                  ? "Updating..."
                  : "Saving..."
                : editingConfig
                  ? "Update Configuration"
                  : "Save Configuration"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
